import { FC, Ref, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form } from 'antd';
import {
	AreaEnum,
	IMobileRef,
	IPOInput,
	IPOMessage,
	IPOTag,
	IPOUtils,
	MobileInput,
} from '@hylaa-npm/ipod';

import { PUpload } from '@/components/PUpload';
import { BASEURL } from '@/apis';

import avatarIcon from '@/images/staff/avatar.svg';

import { CustomModal } from '@/styles/components/CustomModal';
import { AddPopWrap, AddUserPopWrap, FormWrap } from './style';

interface Props {
	userInfo?: {
		email: string;
		name: string;
		name2: string;
		mobile: string;
		avatar: string;
		areaCode: string;
		tags?: IPOTag.ITag[];
		tmpTags?: IPOTag.ITag[];
	};
	onCancel?: () => void;
	isPop?: boolean;
	userRef: Ref<unknown>;
	handleAddUser?: (user: unknown) => void;
	teamTag?: IPOTag.ITag[];
	majorTag?: IPOTag.ITag[];
	isJudgeTag?: boolean;
}
const AddUserPop: FC<Props> = ({
	onCancel,
	isPop = true,
	userRef,
	userInfo,
	handleAddUser,
	teamTag,
	majorTag,
	isJudgeTag = true,
}) => {
	const [form] = Form.useForm();
	const { t } = useTranslation();
	const mobilerRef = useRef<IMobileRef>();
	const [avatar, setAvatar] = useState('');
	const [teamTags, setTeamTags] = useState<IPOTag.ITag[]>([]);
	const [majorTags, setMajorTags] = useState<IPOTag.ITag[]>([]);
	const [teamSelect, setTeamSelect] = useState<string[]>([]);
	const [majorSelect, setMajorSelect] = useState<string[]>([]);
	useEffect(() => {
		if (userInfo) {
			form.setFieldsValue({
				email: userInfo.email,
				name: userInfo.name,
				name2: userInfo.name2,
				mobile: userInfo.mobile,
			});
			if (userInfo.tags) {
				let teamIds: string[] = [];
				let majorIds: string[] = [];
				if (teamTag) {
					teamIds = teamTag.map((item) => item.id);
				}
				if (majorTag) {
					majorIds = majorTag.map((item) => item.id);
				}
				let newTeamTags = userInfo.tags
					.filter((item) => item.type === 2)
					.map((item) => item.id)
					.filter((item) => {
						if (!isJudgeTag) return true;
						return teamIds.includes(item);
					});
				let newMajorTags = userInfo.tags
					.filter((item) => item.type === 1)
					.map((item) => item.id)
					.filter((item) => {
						if (!isJudgeTag) return true;
						return majorIds.includes(item);
					});
				form.setFieldsValue({
					email: userInfo.email,
					name: userInfo.name,
					name2: userInfo.name2,
					mobile: userInfo.mobile,
					major: newMajorTags,
					team: newTeamTags,
				});
				setMajorSelect(newMajorTags);
				setTeamSelect(newTeamTags);
			} else {
				form.setFieldsValue({
					email: userInfo.email,
					name: userInfo.name,
					name2: userInfo.name2,
					mobile: userInfo.mobile,
					major: [],
					team: [],
				});
			}
			setAvatar(userInfo.avatar);
		}
	}, [userInfo]);
	useEffect(() => {
		if (teamTag) {
			setTeamTags(teamTag);
			if (userInfo && userInfo.tags) {
				let teamIds = teamTag.map((item) => item.id);
				let newTeamTags = userInfo.tags
					.filter((item) => item.type === 2)
					.map((item) => item.id)
					.filter((item) => teamIds.includes(item));
				form.setFieldsValue({
					email: form.getFieldValue('email'),
					name: form.getFieldValue('name'),
					name2: form.getFieldValue('name2'),
					mobile: form.getFieldValue('mobile'),
					major: form.getFieldValue('major'),
					team: newTeamTags,
				});
			}
		}
		if (majorTag) {
			setMajorTags(majorTag);
			if (userInfo && userInfo.tags) {
				let teamIds = majorTag.map((item) => item.id);
				let newMajorTags = userInfo.tags
					.filter((item) => item.type === 1)
					.map((item) => item.id)
					.filter((item) => teamIds.includes(item));
				form.setFieldsValue({
					email: form.getFieldValue('email'),
					name: form.getFieldValue('name'),
					name2: form.getFieldValue('name2'),
					mobile: form.getFieldValue('mobile'),
					major: newMajorTags,
					team: form.getFieldValue('team'),
				});
			}
		}
	}, [JSON.stringify(teamTag), JSON.stringify(majorTag)]);
	useImperativeHandle(userRef, () => ({
		handleUpdateUser: () => {
			return handleOk();
		},
	}));
	const handleOk = () => {
		if (form.getFieldValue('email')) {
			let emailReg = /^\w+@[a-z0-9]+\.[a-z]+$/i;
			if (!emailReg.test(form.getFieldValue('email'))) {
				IPOMessage.error(t('common.emailError'));
				return;
			}
		}
		if (majorSelect.length === 0) {
			IPOMessage.error(t('common.selectMajorTag'));
			return;
		}
		if (teamSelect.length === 0) {
			IPOMessage.error(t('common.selectTeamTag'));
			return;
		}
		let info = {
			name: form.getFieldValue('name'),
			name2: form.getFieldValue('name2'),
			email: form.getFieldValue('email'),
			avatar: avatar,
			major: majorSelect,
			team: teamSelect,
		};
		if (handleAddUser) {
			handleAddUser(info);
			return;
		}
		return info;
	};

	const FormDom = () => {
		return (
			<Form
				form={form}
				name="basic"
				labelCol={{ span: 4 }}
				wrapperCol={{ span: 20 }}
				initialValues={{ remember: true }}
				autoComplete="off">
				<Form.Item label={t('common.avatar')} name="avatar" className="adduser-avatar-wrap">
					<FormWrap>
						<PUpload handleUploadPre={(data) => setAvatar(data.id)} accept=".png,.jpeg,.jpg,.webp">
							{avatar ? (
								<img src={IPOUtils.getFileUrl(BASEURL, avatar)} alt="" />
							) : (
								<img src={avatarIcon} alt="" />
							)}
						</PUpload>
					</FormWrap>
				</Form.Item>
				<Form.Item label={t('common.eName')} name="name">
					<IPOInput />
				</Form.Item>
				<Form.Item label={t('common.cName')} name="name2">
					<IPOInput />
				</Form.Item>
				<Form.Item label={t('common.phone')} name="mobile">
					<MobileInput
						disabled
						mobileRef={mobilerRef}
						mobileInfo={{
							areaCode: (userInfo?.areaCode as AreaEnum) || '',
							phoneNum: userInfo?.mobile || '',
						}}
					/>
				</Form.Item>
				<Form.Item label={t('common.email')} name="email">
					<IPOInput />
				</Form.Item>
				<Form.Item label={t('common.major')} name="major">
					<IPOTag.SelectTag
						dataList={majorTags}
						onChange={(list: unknown) => {
							setMajorSelect((list as unknown) as string[]);
						}}></IPOTag.SelectTag>
				</Form.Item>
				<Form.Item label={t('common.team')} name="team">
					<IPOTag.SelectTag
						dataList={teamTags}
						onChange={(list: unknown) => {
							setTeamSelect((list as unknown) as string[]);
						}}></IPOTag.SelectTag>
				</Form.Item>
			</Form>
		);
	};
	if (isPop) {
		return (
			<AddUserPopWrap>
				<CustomModal
					maskClosable={false}
					getContainer={false}
					isShowPadding={true}
					title={t('')}
					visible={true}
					onOk={() => handleOk()}
					onCancel={onCancel}>
					<AddPopWrap>{FormDom()}</AddPopWrap>
				</CustomModal>
			</AddUserPopWrap>
		);
	} else {
		return FormDom();
	}
};
export default AddUserPop;
